import React, { useRef, useEffect } from 'react';
import { Send, Mic, MicOff } from 'lucide-react';
import { useSpeech } from '../../hooks/useSpeech';

const ChatInputBar = ({ inputText, setInputText, onSend, onStop, isAiTyping }) => {
    const { isListening, toggleListening } = useSpeech(setInputText);
    const textareaRef = useRef(null);

    // 입력 내용 길이에 따른 textarea 자동 높이 조절
    useEffect(() => {
        const textarea = textareaRef.current;
        if (textarea) {
            // 높이를 먼저 초기화해야 줄어들 때도 올바르게 계산 가능
            textarea.style.height = 'auto';
            const scrollHeight = textarea.scrollHeight;
            const minHeight = 48;
            const maxHeight = 116; // 4줄 정도 높이 (22.5 * 4 + 26)

            if (scrollHeight > maxHeight) {
                textarea.style.height = `${maxHeight}px`;
                textarea.style.overflowY = 'auto';
            } else {
                textarea.style.height = `${Math.max(scrollHeight, minHeight)}px`;
                textarea.style.overflowY = 'hidden';
            }
        }
    }, [inputText]);

    const handleKeyDown = (e) => {
        // IME 한글 조합 중이거나 방금 조합이 끝난 찰나의 Enter 무시 (브라우저 고질병 방어)
        if (e.nativeEvent.isComposing || e.keyCode === 229) return;

        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();

            if (inputText.trim() && !isAiTyping) {
                onSend();
            }
        }
    };

    return (
        <div className="pt-1 px-3 pb-4 sm:pb-6 bg-white border-t border-gray-100 flex-shrink-0 relative z-20">
            <div className="max-w-4xl mx-auto relative">
                {/* 입력창 */}
                <textarea
                    ref={textareaRef}
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isAiTyping}
                    placeholder={
                        isAiTyping ? "AI가 조문을 분석하여 답변을 생성하고 있습니다..." :
                            isListening ? "듣고 있습니다..." :
                                "산업안전 AI 알리미에게 물어봐주세요."
                    }
                    className={`w-full border rounded-2xl pl-4 sm:pl-5 pr-24 sm:pr-28 py-[14px] outline-none resize-none text-base sm:text-base placeholder:text-base sm:placeholder:text-base transition-colors duration-300 custom-scrollbar leading-normal
                        ${isAiTyping ? 'bg-gray-100/70 border-gray-100 text-gray-400 cursor-not-allowed placeholder:text-gray-400/70' :
                            isListening ? 'bg-[#f8fafc] border-red-300 ring-2 ring-red-50 text-gray-800' :
                                'bg-[#f8fafc] border-gray-200 focus:border-blue-400 focus:bg-white text-gray-800'
                        }`}
                    rows={1}
                />

                {/* 우측 버튼 영역 */}
                <div className="absolute right-2 top-[24px] -translate-y-1/2 flex items-center gap-1">

                    {/* 마이크 버튼 */}
                    <button
                        onClick={toggleListening}
                        disabled={isAiTyping}
                        className={`p-2 sm:p-2.5 rounded-xl transition-all ${isListening
                            ? 'bg-red-50 text-red-500 animate-pulse'
                            : 'text-gray-400 hover:text-blue-500 hover:bg-blue-50'
                            }`}
                        title="음성으로 질문하기"
                    >
                        {isListening ? <Mic size={18} className="sm:w-5 sm:h-5" /> : <MicOff size={18} className="sm:w-5 sm:h-5" />}
                    </button>

                    {/* 전송 버튼 */}
                    <button
                        onClick={onSend}
                        disabled={!inputText.trim() || isAiTyping}
                        className={`p-2 sm:p-2.5 rounded-xl transition-all ${inputText.trim() && !isAiTyping
                            ? 'bg-blue-600 text-white shadow-md shadow-blue-200 hover:bg-blue-700'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            }`}
                    >
                        <Send size={18} className={`sm:w-5 sm:h-5 ${inputText.trim() && !isAiTyping ? 'translate-x-0.5' : ''}`} />
                    </button>
                </div>
            </div>

            {/* 하단 안내 문구 */}
            <div className="text-center mt-2 px-4">
                <span className="text-sm sm:text-sm text-gray-400 font-medium tracking-tight leading-snug block">
                    산업안전 AI 알리미는 중대재해처벌법 및 산업안전보건법을 근거로 답변합니다. 중요한 결정 전 전문가와 상담하세요.
                </span>
            </div>
        </div>
    );
};

export default ChatInputBar;
